import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { Timestamp } from 'firebase/firestore';
import { useLoadingContext } from '../../../../../context/loading/LoadingContext';
import { Select, MenuItem, InputLabel, FormControl, InputAdornment } from '@mui/material';
import { useUserContext } from '../../../../../context/user_context/UserContext';
import BaseModal from '../../../../../components/modals/BaseModal';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { useOrganizationContext } from '../../../../../context/organization_context/OrganizationContext';
import Grid from '@mui/material/Grid';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MuiTelInput } from 'mui-tel-input';
import { ToastType, useToast } from '../../../../../context/toast/ToastContext';
import { CloudFunctions } from '../../../../../utils/enums/CloudFunctions';
import AddDepartmentModal from '../../Departments/modals/AddDepartmentModal';
import { IconPlus } from '@tabler/icons-react';
import { StringKeys } from '../../../../../resources/strings/StringKeys';
import { useTranslation } from 'react-i18next';
import AddRoleModal from '../../../Roles/modals/AddRoleModal';

const AddEmployeeModal = ({ show, onHide, organization }) => {
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [birthday, setBirthday] = useState(null);
    const [hireDate, setHireDate] = useState(null); // Initialize as null
    const [employeeTitle, setEmployeeTitle] = useState("");
    const [employeeRate, setEmployeeRate] = useState("");
    const [employeeId, setEmployeeId] = useState("");
    const [department, setDepartment] = useState("");
    const [employeeEmail, setEmployeeEmail] = useState("");
    const [selectedRole, setSelectedRole] = useState("");
    const [firstNameError, setFirstNameError] = useState(false);
    const [lastNameError, setLastNameError] = useState(false);
    const [phoneNumberError, setPhoneNumberError] = useState(false);
    const [emailError, setEmailError] = useState(false);
    const [employeeTitleError, setEmployeeTitleError] = useState(false);
    const [employeeIdError, setEmployeeIdError] = useState(false);
    const [roleIdError, setRoleIdError] = useState(false);
    const [showAddDepartmentModal, setShowAddDepartmentModal] = useState(false);
    const [showAddRoleModal, setShowAddRoleModal] = useState(false);

    const { setLoadingData } = useLoadingContext();
    const { firebaseUser } = useUserContext();
    const { organizationDepartments, organizationRoles, addRole } = useOrganizationContext();
    const { showToast } = useToast();
    const functions = getFunctions();
    const { t } = useTranslation();

    const checkValidations = () => {
        let valid = true;
        if (!firstName.trim()) {
            setFirstNameError(true);
            valid = false;
        }
        if (!lastName.trim()) {
            setLastNameError(true);
            valid = false;
        }
        if (!phoneNumber.trim()) {
            setPhoneNumberError(true);
            valid = false;
        }
        if (!employeeEmail.trim()) {
            setEmailError(true);
            valid = false;
        }
        // if (!employeeTitle.trim()) {
        //     setEmployeeTitleError(true);
        //     valid = false;
        // }
        if (!selectedRole.trim()) {
            setRoleIdError(true);
            valid = false;
        }
        return valid
    }

    const handleSubmit = async () => {
        setLoadingData(true);

        if(!checkValidations()) {
            setLoadingData(false)
            return
        }

        const birthdayTimestamp = birthday ? Timestamp.fromDate(birthday.toDate()) : null;
        const hireDateTimestamp = hireDate ? Timestamp.fromDate(hireDate.toDate()) : null;

        const item = {
            first_name: firstName,
            last_name: lastName,
            department_id: department,
            birthday: birthdayTimestamp,
            phone_number: phoneNumber,
            hire_date: hireDateTimestamp,
            active: true,
            availability: {
                "SUN": { "restrictions": "none" },
                "MON": { "restrictions": "none" },
                "TUE": { "restrictions": "none" },
                "WED": { "restrictions": "none" },
                "THU": { "restrictions": "none" },
                "FRI": { "restrictions": "none" },
                "SAT": { "restrictions": "none" }
            },
            // title: employeeTitle,
            // rate: employeeRate,
            role: selectedRole
        };

        const addEmployeeFunction = httpsCallable(functions, CloudFunctions.AddEmployee);
        try {
            const result = await addEmployeeFunction({
                email: employeeEmail,
                name: `${firstName} ${lastName}`,
                organizationId: firebaseUser.organization,
                item,
            });
            showToast(result.data.message, ToastType.SUCCESS);
            resetForm();
            onHide();
        } catch (error) {
            console.error('Error adding employee:', error);
            showToast('Failed to add employee.', ToastType.DANGER);
        } finally {
            setLoadingData(false);
        }
    };

    const resetForm = () => {
        setFirstName("");
        setLastName("");
        setPhoneNumber("");
        setHireDate(null);
        setEmployeeEmail("");
        setEmployeeTitle("");
        setBirthday(null);
        setDepartment("");
        setEmployeeRate("");
        setEmployeeId("");
        setSelectedRole("");
        setFirstNameError(false);
        setLastNameError(false);
        setPhoneNumberError(false);
        setEmailError(false);
        setEmployeeTitleError(false);
        setEmployeeIdError(false);
    };

    const handleDepartmentChange = (e) => {
        if (e.target.value === 'addDepartment') {
            setShowAddDepartmentModal(true);
        } else {
            setDepartment(e.target.value);
        }
    };

    const departmentOptions = organizationDepartments.map((dept) => (
        <MenuItem key={dept.id} value={dept.id}>{dept.name}</MenuItem>
    ));    

    if (!organization) return null;

    const actions = (
        <Button onClick={handleSubmit}>{t(StringKeys.SUBMIT)}</Button>
    );

    const handlePhoneNumberChange = (newValue) => {
        setPhoneNumber(newValue);
    };

    const handleAddRole = async (newRole) => {
        await addRole(newRole, showToast);
        setShowAddRoleModal(false);
        setSelectedRole(newRole);
    };

    return (
        <>
            <BaseModal
                open={show}
                onClose={() => {
                    resetForm();
                    onHide();
                }}
                title={t(StringKeys.ADD_EMPLOYEE)}
                actions={actions}
            >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <TextField
                        margin="dense"
                        id="first-name"
                        label={t(StringKeys.FIRST_NAME)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={firstName}
                        onChange={(e) => {
                            setFirstName(e.target.value);
                            if (firstNameError) setFirstNameError(false);
                        }}
                        error={firstNameError}
                        helperText={firstNameError ? t(StringKeys.FIRST_NAME_IS_REQUIRED) : ""}
                    />
                    <TextField
                        margin="dense"
                        id="last-name"
                        label={t(StringKeys.LAST_NAME)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={lastName}
                        onChange={(e) => {
                            setLastName(e.target.value);
                            if (lastNameError) setLastNameError(false);
                        }}
                        error={lastNameError}
                        helperText={lastNameError ? t(StringKeys.LAST_NAME_IS_REQUIRED) : ""}
                    />
                    <MuiTelInput
                        label={t(StringKeys.PHONE)}
                        defaultCountry='US'
                        fullWidth
                        margin="dense"
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        error={phoneNumberError}
                        helperText={phoneNumberError ? t(StringKeys.PHONE_NUMBER_IS_REQUIRED) : ""}
                    />
                    <TextField
                        margin="dense"
                        id="email"
                        label={t(StringKeys.EMAIL)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={employeeEmail}
                        onChange={(e) => {
                            setEmployeeEmail(e.target.value);
                            if (emailError) setEmailError(false);
                        }}
                        error={emailError}
                        helperText={emailError ? t(StringKeys.EMAIL_IS_REQUIRED) : ""}
                    />
                    {/* <TextField
                        margin="dense"
                        id="employee-title"
                        label={t(StringKeys.TITLE)}
                        type="text"
                        fullWidth
                        variant="outlined"
                        value={employeeTitle}
                        onChange={(e) => {
                            setEmployeeTitle(e.target.value);
                            if (employeeTitleError) setEmployeeTitleError(false);
                        }}
                        error={employeeTitleError}
                        helperText={employeeTitleError ? t(StringKeys.TITLE_IS_REQUIRED) : ""}
                    /> */}
                    <FormControl fullWidth margin="dense">
                        <InputLabel id="department-select-label">{t(StringKeys.DEPARTMENT)}</InputLabel>
                        <Select
                            labelId="department-select-label"
                            id="department-select"
                            value={department}
                            label={t(StringKeys.DEPARTMENT)}
                            onChange={handleDepartmentChange}
                            variant="outlined"
                        >
                            {departmentOptions}
                            <MenuItem key="addDepartment" value="addDepartment">
                                <IconPlus /> {t(StringKeys.ADD_DEPARTMENT)}
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl
                        error={roleIdError}
                        fullWidth 
                        margin="dense">
                        <InputLabel>{t(StringKeys.ROLE)}</InputLabel>
                        <Select
                            label={t(StringKeys.ROLE)}
                            error={roleIdError}
                            helperText={roleIdError ? t(StringKeys.EMPLOYEE_ROLE_IS_REQUIRED) : ""}
                            value={selectedRole}
                            onChange={(e) => {
                                if(e.target.value === 'addRole') {
                                    setShowAddRoleModal(true)
                                } else {
                                    setSelectedRole(e.target.value)
                                }
                            }}
                        >
                            {Object.keys(organizationRoles)
                                .filter(role => role !== 'Owner')
                                .map((role) => (
                                    <MenuItem key={role} value={role}>
                                        {role}
                                    </MenuItem>
                                ))}
                            <MenuItem value="addRole">
                                <IconPlus /> {t(StringKeys.ADD_ROLE)}
                            </MenuItem>
                        </Select>
                    </FormControl>
                    <DatePicker
                        label={t(StringKeys.HIRE_DATE)}
                        value={hireDate}
                        onChange={(newValue) => {
                            setHireDate(newValue);
                        }}
                        sx={{ width: "100%", marginTop: "16px", marginBottom: "8px" }}
                    />
                    <DatePicker
                        label={t(StringKeys.BIRTHDAY)}
                        value={birthday}
                        onChange={(newValue) => {
                            setBirthday(newValue);
                        }}
                        sx={{ width: "100%", marginTop: "8px", marginBottom: "8px" }}
                    />
                    {/* <TextField
                        margin="dense"
                        id="employee-rate"
                        label={t(StringKeys.HOURLY_RATE)}
                        type="number"
                        InputProps={{
                            startAdornment: <InputAdornment position="start">$</InputAdornment>,
                            inputProps: {
                                min: "0",
                                step: ".01"
                            }
                        }}
                        fullWidth
                        variant="outlined"
                        value={employeeRate}
                        onChange={(e) => setEmployeeRate(e.target.value)}
                    /> */}
                </LocalizationProvider>
            </BaseModal>
            <AddDepartmentModal
                show={showAddDepartmentModal}
                onHide={() => setShowAddDepartmentModal(false)}
            />
            <AddRoleModal
                show={showAddRoleModal}
                onHide={() => setShowAddRoleModal(false)}
                onSubmit={handleAddRole}
                shouldRedirect={false}
            />
        </>
    );
};

export default AddEmployeeModal;
